<template>
  <div style="flex: 1 1 0px">
    <v-dialog
      v-model="dialog"
      width="1200"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="stat-card" v-on="on" v-bind="attrs">
          <div class="stat-card__top">
            <div class="stat-card__number">
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="24"
                width="2"
                color="accent"
              ></v-progress-circular>
              <span v-else>{{ properties.length }}</span>
            </div>
            <div class="stat-card__icon">
              <hero-icon :icon="HomeIcon" :size="20" />
            </div>
          </div>

          <div class="stat-card__label">Propiedades asignadas</div>
        </div>
      </template>

      <v-card class="warm-dialog" rounded="lg">
        <div class="dialog-head">
          <div class="dialog-head-text">
            <h2 class="dialog-title">Propiedades</h2>
            <p class="dialog-meta">{{ properties.length }} asignadas</p>
          </div>

          <button type="button" class="ghost-close" @click="dialog = false">
            <hero-icon :icon="XMarkIcon" :size="18" />
          </button>
        </div>

        <div class="hairline"></div>

        <div class="dialog-body">
          <div v-if="!properties.length && !loading" class="empty-state">
            <div class="empty-icon">
              <hero-icon :icon="HomeIcon" :size="26" />
            </div>
            <p class="empty-text">Sin propiedades asignadas</p>
          </div>

          <list
            v-else
            ref="list"
            @reload="fetchProperties('no-cache')"
            :properties="properties"
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { AssignedProperties } from "@/graphql/queries/staff";
import List from "@/components/properties/List";
import HeroIcon from "@/components/shared/HeroIcon";
import HomeIcon from "heroicons/24/outline/home.svg";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";

export default {
  data: () => ({
    dialog: false,
    properties: [],
    loading: false,
    loaded: false,
    HomeIcon,
    XMarkIcon
  }),

  props: {
    id: {
      required: true,
      type: Number | String
    }
  },

  watch: {
    id: {
      handler() {
        // New staff selected: reset the guard so the new id's data is fetched,
        // then fetch the count (cache-first serves already-seen ids without a network hit).
        this.loaded = false;
        this.fetchProperties();
      }
    }
  },

  created() {
    this.fetchProperties();
  },

  methods: {
    fetchProperties(fetchPolicy = "cache-first") {
      // Guard against redundant work: the count/list for a given id is only
      // fetched once (cache-first). The explicit @reload path passes 'no-cache'
      // to bypass the guard and force a fresh post-mutation refetch.
      if (this.loaded && fetchPolicy !== "no-cache") return;

      this.loading = true;

      this.$apollo
        .query({
          query: AssignedProperties,
          variables: {
            id: this.id
          },
          fetchPolicy
        })
        .then(res => {
          this.properties = res.data.assignedProperties.properties;
          this.loaded = true;
          this.loading = false;
        });
    }
  },

  components: { List, HeroIcon }
};
</script>

<style scoped>
/* ===== Canonical Gestiones stat card (shared across all four analytics cards) ===== */
.stat-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.stat-card:hover {
  border-color: #dcd4c6;
  box-shadow: 0 12px 28px -16px rgba(40, 33, 22, 0.24);
  transform: translateY(-1px);
}

.stat-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.stat-card__number {
  display: flex;
  align-items: center;
  min-height: 30px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  color: #1a1814;
}

.stat-card__icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #faf8f4;
  color: #b52118;
}

.stat-card__label {
  font-size: 13.5px;
  color: #756f64;
}

/* Dialog chrome */
.warm-dialog {
  background: #ffffff;
}

.dialog-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px 16px;
}

.dialog-head-text {
  min-width: 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: #1a1814;
  margin: 0;
}

.dialog-meta {
  font-size: 13px;
  color: #756f64;
  margin: 2px 0 0;
}

.ghost-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  color: #756f64;
  background: transparent;
  transition: background 0.15s ease, color 0.15s ease;
  flex: none;
}

.ghost-close:hover {
  background: #f1ece3;
  color: #1a1814;
}

.hairline {
  height: 1px;
  background: #ece7de;
}

.dialog-body {
  padding: 18px 22px 22px;
  max-height: 78vh;
  overflow-y: auto;
  background: #faf8f4;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #f1ece3;
  color: #a39a8c;
  margin-bottom: 14px;
}

.empty-text {
  font-size: 14px;
  color: #756f64;
  margin: 0;
}

@media (max-width: 600px) {
  .dialog-body {
    max-height: none;
    flex: 1 1 auto;
  }
}
</style>
